import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Archive, Ban, BarChart3, CalendarRange, Minus, Pencil, Plus, SkipForward, Timer, Trash2, Undo2 } from 'lucide-react';
import { addDays, checkMove, logicalDateFor, type LocalDate, type TodayItem } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { archiveHabit, dayTotal, deleteHabit, logAmount, moveOccurrence, setOverride, undoMove } from '../domain/actions';
import { toEngineSettings, toHabitDef, type HabitRec } from '../domain/model';
import { getMeta, setMeta } from '../db/db';
import { Sheet } from '../ui/Sheet';
import { Field } from '../ui/Field';
import { useToast } from '../ui/Toast';
import { errorMessage, unitLabel } from '../ui/format';

interface TimerState {
  startedAt: number;
  accumulatedMs: number;
  running: boolean;
}

export function HabitActions({ habit, item, open, onClose }: { habit: HabitRec; item: TodayItem; open: boolean; onClose: () => void }) {
  const { t, m } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'menu' | 'amount' | 'skip' | 'timer' | 'move'>('menu');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const ue = item.ue;
  const v = ue?.version;
  const unit = unitLabel(m, v?.unit);
  const numeric = habit.kind !== 'task' && (v?.goalType === 'numeric' || habit.kind === 'quit');
  const timerable = habit.kind === 'build' && (v?.unit === 'min' || v?.unit === 'h');
  // Trasladar solo tiene sentido en una UE diaria aún sin resolver (FR-SCH-12).
  const movable = !!ue && ue.kind === 'day' && habit.kind !== 'task' && ['pending', 'missed'].includes(ue.status);
  const movedHere = data.moves.find((mo) => mo.habitId === habit.id && mo.toDate === ctx.today);

  // Al cerrarse, la hoja vuelve al menú inicial (ajuste de estado durante el render).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) {
      setMode('menu');
      setAmount('');
      setReason('');
    }
  }

  const run = async (fn: () => Promise<unknown>, msg?: string) => {
    try {
      await fn();
      if (msg) toast.show(msg);
      onClose();
    } catch (err) {
      toast.show(errorMessage(t, err));
    }
  };

  const today = ctx.today;
  const total = dayTotal(data.entries, habit.id, today);

  return (
    <Sheet open={open} onClose={onClose} title={habit.name}>
      {mode === 'menu' && (
        <div className="menu">
          {numeric && (
            <>
              <button type="button" className="menu__item" onClick={() => setMode('amount')}>
                <Plus size={20} aria-hidden="true" /> {t('habit.actions.record')}
              </button>
              {total > 0 && (
                <button type="button" className="menu__item" onClick={() => run(() => logAmount(habit, today, -habit.defaultIncrement, ctx, data.entries, data.overrides))}>
                  <Minus size={20} aria-hidden="true" /> {t('habit.actions.decrement', { amount: `${habit.defaultIncrement}${unit ? ` ${unitLabel(m, v?.unit, habit.defaultIncrement)}` : ''}` })}
                </button>
              )}
            </>
          )}
          {timerable && (
            <button type="button" className="menu__item" onClick={() => setMode('timer')}>
              <Timer size={20} aria-hidden="true" /> {t('habit.actions.timer')}
            </button>
          )}
          {ue && ue.status !== 'skipped' && (
            <>
              <button type="button" className="menu__item" onClick={() => setMode('skip')}>
                <SkipForward size={20} aria-hidden="true" /> {t('habit.actions.skip')}
              </button>
            </>
          )}
          {ue && ue.status === 'skipped' && (
            <button type="button" className="menu__item" onClick={() => run(() => setOverride(habit, today, null, ctx), t('habit.unskipToast'))}>
              <Undo2 size={20} aria-hidden="true" /> {t('habit.actions.unskip')}
            </button>
          )}
          {movable && (
            <button type="button" className="menu__item" onClick={() => setMode('move')}>
              <CalendarRange size={20} aria-hidden="true" /> {t('habit.actions.move')}
            </button>
          )}
          {movedHere && (
            <button type="button" className="menu__item" onClick={() => run(() => undoMove(movedHere), t('habit.moveUndoneToast'))}>
              <Undo2 size={20} aria-hidden="true" /> {t('habit.actions.undoMove')}
            </button>
          )}
          {ue && ue.status !== 'failed' && (
            <>
              <button type="button" className="menu__item" onClick={() => run(() => setOverride(habit, today, 'failed', ctx))}>
                <Ban size={20} aria-hidden="true" /> {t('habit.actions.fail')}
              </button>
              <p className="menu__hint">{t('habit.failHelp')}</p>
            </>
          )}
          {ue && ue.status === 'failed' && (
            <button type="button" className="menu__item" onClick={() => run(() => setOverride(habit, today, null, ctx))}>
              <Undo2 size={20} aria-hidden="true" /> {t('habit.actions.unfail')}
            </button>
          )}
          <button type="button" className="menu__item" onClick={() => navigate(`/habits/${habit.id}`)}>
            <BarChart3 size={20} aria-hidden="true" /> {t('habit.actions.details')}
          </button>
          <button type="button" className="menu__item" onClick={() => navigate(`/habits/${habit.id}/edit`)}>
            <Pencil size={20} aria-hidden="true" /> {t('habit.actions.edit')}
          </button>
          <button type="button" className="menu__item" onClick={() => run(() => archiveHabit(habit, ctx, true), t('habit.archivedToast'))}>
            <Archive size={20} aria-hidden="true" /> {t('habit.actions.archive')}
          </button>
          <button
            type="button"
            className="menu__item menu__item--danger"
            onClick={async () => {
              try {
                await deleteHabit(habit, true);
                onClose();
                toast.show(t('habit.deletedToast'), { label: t('common.undo'), run: () => void deleteHabit(habit, false) });
              } catch (err) {
                toast.show(errorMessage(t, err));
              }
            }}
          >
            <Trash2 size={20} aria-hidden="true" /> {t('habit.actions.delete')}
          </button>
        </div>
      )}

      {mode === 'amount' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(amount.replace(',', '.'));
            if (!Number.isFinite(n) || n === 0) return;
            void run(() => logAmount(habit, today, n, ctx, data.entries, data.overrides), t('habit.loggedToast'));
          }}
        >
          <Field label={`${t('detail.amount')}${unit ? ` (${unit})` : ''}`} help={t('habit.progress', { value: total, target: ue?.required ?? 0 })}>
            {(p) => <input {...p} className="input num" inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />}
          </Field>
          <button type="submit" className="btn btn--primary btn--block">
            {t('habit.actions.record')}
          </button>
        </form>
      )}

      {mode === 'skip' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => setOverride(habit, today, 'skipped', ctx, reason.trim() || null), t('habit.skippedToast'));
          }}
        >
          <p className="muted small">{t('habit.skipHelp')}</p>
          <Field label={`${t('habit.reasonLabel')} (${t('common.optional')})`}>
            {(p) => <input {...p} className="input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <button type="submit" className="btn btn--primary btn--block">
            {t('habit.actions.skip')}
          </button>
        </form>
      )}

      {mode === 'move' && <MovePanel habit={habit} from={today} onDone={onClose} />}

      {mode === 'timer' && <TimerPanel habit={habit} onDone={onClose} />}
    </Sheet>
  );
}

/**
 * Traslada una ocurrencia a otro día sin tocar la repetición (FR-SCH-12).
 * Se ofrecen los próximos días libres, porque es la elección habitual, y además
 * una fecha concreta para cualquier otro caso. El motor valida las dos vías.
 */
export function MovePanel({ habit, from, onDone }: { habit: HabitRec; from: LocalDate; onDone: () => void }) {
  const { t, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const def = toHabitDef(habit);
  const settings = toEngineSettings(ctx.settings);
  const moves = data.moves.filter((m) => m.habitId === habit.id);

  // Los 14 días siguientes al origen, quedándose con los 5 primeros que el motor acepta.
  const options: LocalDate[] = [];
  for (let i = 1; i <= 14 && options.length < 5; i++) {
    const d = addDays(from, i);
    if (!checkMove(def, settings, moves, from, d)) options.push(d);
  }

  const [date, setDate] = useState<LocalDate>(options[0] ?? addDays(from, 1));
  const rejection = checkMove(def, settings, moves, from, date);

  const apply = (to: LocalDate) =>
    void (async () => {
      try {
        await moveOccurrence(habit, moves, from, to, ctx);
        toast.show(t('habit.movedToast', { date: fmtDate(to) }));
        onDone();
      } catch (err) {
        toast.show(errorMessage(t, err));
      }
    })();

  return (
    <div className="stack">
      <p className="muted small">{t('habit.moveHelp')}</p>
      {options.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap' }} role="group" aria-label={t('habit.moveSoonest')}>
          {options.map((d) => (
            <button key={d} type="button" className="btn btn--outline btn--sm" onClick={() => apply(d)}>
              {fmtDate(d, { weekday: 'short', day: 'numeric', month: 'short' })}
            </button>
          ))}
        </div>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!rejection) apply(date);
        }}
      >
        <Field label={t('habit.moveDateLabel')} error={rejection ? t(`errors.${rejection}`) : undefined}>
          {(p) => <input {...p} type="date" className="input" value={date} onChange={(e) => setDate(e.target.value as LocalDate)} />}
        </Field>
        <button type="submit" className="btn btn--primary btn--block" disabled={!!rejection}>
          {t('habit.actions.move')}
        </button>
      </form>
    </div>
  );
}

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${h > 0 ? `${h}:` : ''}${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Temporizador persistente: sobrevive a recargas y a la suspensión de la pestaña (FR-LOG-05). */
function TimerPanel({ habit, onDone }: { habit: HabitRec; onDone: () => void }) {
  const { t } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const key = `timer:${habit.id}`;
  const [state, setState] = useState<TimerState | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    void getMeta<TimerState | null>(key, null).then(setState);
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [key]);

  const save = async (s: TimerState | null) => {
    setState(s);
    await setMeta(key, s);
  };
  const elapsed = state ? state.accumulatedMs + (state.running ? now - state.startedAt : 0) : 0;
  const v = [...habit.versions].pop();

  return (
    <div className="stack">
      <div className="timer" role="timer" aria-live="off">
        {fmtElapsed(elapsed)}
      </div>
      <div className="row">
        {!state && (
          <button type="button" className="btn btn--primary btn--block" onClick={() => save({ startedAt: Date.now(), accumulatedMs: 0, running: true })}>
            {t('timer.start')}
          </button>
        )}
        {state?.running && (
          <button type="button" className="btn btn--block" onClick={() => save({ ...state, running: false, accumulatedMs: elapsed })}>
            {t('timer.pause')}
          </button>
        )}
        {state && !state.running && (
          <button type="button" className="btn btn--block" onClick={() => save({ ...state, running: true, startedAt: Date.now() })}>
            {t('timer.resume')}
          </button>
        )}
      </div>
      {state && (
        <div className="row">
          <button type="button" className="btn btn--ghost" onClick={() => save(null)}>
            {t('timer.discard')}
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn--primary"
            onClick={async () => {
              const minutes = elapsed / 60_000;
              const value = v?.unit === 'h' ? Math.round((minutes / 60) * 100) / 100 : Math.max(1, Math.round(minutes));
              // El registro pertenece al día lógico en que empezó el temporizador.
              const firstStart = state.running ? state.startedAt - state.accumulatedMs : Date.now() - elapsed;
              const day = logicalDateFor(firstStart, ctx.tz, ctx.settings.dayStartMinutes);
              try {
                await logAmount(habit, day < habit.startDate ? ctx.today : day, value, ctx, data.entries, data.overrides);
                await save(null);
                toast.show(t('habit.loggedToast'));
                onDone();
              } catch (err) {
                toast.show(errorMessage(t, err));
              }
            }}
          >
            {t('timer.stop')}
          </button>
        </div>
      )}
    </div>
  );
}
